import { useState, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../../services/api";

function telPrincipal(contato) {
  const t = (contato.telefones||[]).find(x=>x.principal) || (contato.telefones||[])[0];
  return t ? t.numero : "-";
}
function endPrincipal(contato) {
  return (contato.enderecos||[]).find(x=>x.principal) || (contato.enderecos||[])[0];
}
function enderecoCompleto(end) {
  if(!end) return "-";
  return `${end.logradouro}, ${end.numero}${end.bairro ? " - "+end.bairro : ""}, ${end.cidade}/${end.estado}`;
}
function coordValida(end){
  if(!end || end.latitude == null || end.longitude == null) return null;
  const lat = parseFloat(end.latitude);
  const lng = parseFloat(end.longitude);
  if(!isFinite(lat) || !isFinite(lng)) return null;
  return [lat, lng];
}
function corPorStatus(c){
  if(c.status === "PROSPECCAO") return c.data_ultima_interacao ? "#f59e0b" : "#dc2626";
  if(c.status === "ATIVO") return "#16a34a";
  return "#6b7280";
}
function statusLabel(c){
  const labels = { ATIVO:"Ativo", INATIVO:"Inativo", BLOQUEADO:"Bloqueado", PROSPECCAO:"Prospeccao" };
  return labels[c.status] || c.status;
}
function criarIcone(cor, destaque){
  const tamanho = destaque ? 22 : 14;
  const anel = destaque ? `box-shadow:0 0 0 4px ${cor}66, 0 1px 6px rgba(0,0,0,0.4);` : `box-shadow:0 1px 4px rgba(0,0,0,0.3);`;
  return L.divIcon({html:`<div style="background:${cor};width:${tamanho}px;height:${tamanho}px;border-radius:50%;border:2px solid #fff;${anel}"></div>`,iconSize:[tamanho,tamanho],className:""});
}
function fmtCompra(p){
  const data = p.data_pedido ? new Date(p.data_pedido+"T12:00:00").toLocaleDateString("pt-BR") : "-";
  const valor = parseFloat(p.valor_total||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2});
  return { data, valor };
}
const STA_BADGE = { ATIVO:"badge-green", INATIVO:"badge-gray", BLOQUEADO:"badge-red", PROSPECCAO:"badge-amber" };

function LinhaCliente({ c, selecionado, onSelecionar }){
  const [open, setOpen] = useState(false);
  const [compras, setCompras] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const handleClick = async () => {
    onSelecionar(c);
    const abrindoAgora = !open;
    setOpen(abrindoAgora);
    if(abrindoAgora && compras === null){
      setCarregando(true);
      try{
        const res = await api.get("/vendas/pedidos/?cliente="+c.id+"&page_size=5");
        setCompras(res.data.results || res.data);
      }catch{ setCompras([]); }
      finally{ setCarregando(false); }
    }
  };

  const end = endPrincipal(c);

  return (
    <>
      <tr className="tr-expandable" style={{background: selecionado ? "#eff6ff" : ""}} onClick={handleClick}>
        <td><strong>{c.nome}</strong></td>
        <td><span className="badge badge-amber">{c.categoria === "LOJISTA" ? "Lojista" : "Atacadista"}</span></td>
        <td><span className={"badge "+(STA_BADGE[c.status]||"badge-gray")}>{statusLabel(c)}</span></td>
        <td className="fs-12 text-secondary">{telPrincipal(c)}</td>
        <td className="fs-12 text-secondary">
          {c.data_ultima_interacao ? new Date(c.data_ultima_interacao+"T12:00:00").toLocaleDateString("pt-BR") : "-"}
        </td>
      </tr>
      {open && (
        <tr className="tr-dropdown">
          <td colSpan={5}>
            <div className="dropdown-content">
              <div className="fs-13 mb-8"><i className="ti ti-map-pin"/> {enderecoCompleto(end)}</div>
              <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:"var(--z-text-secondary)"}}>Ultimas compras</div>
              {carregando ? (
                <div className="fs-12 text-secondary">Carregando...</div>
              ) : !compras || !compras.length ? (
                <div className="fs-12 text-secondary">Nenhuma compra registrada.</div>
              ) : (
                <table style={{width:"100%",fontSize:12}}>
                  <thead><tr className="bg-page"><th className="td-l">Data</th><th className="td-r">Valor</th></tr></thead>
                  <tbody>
                    {compras.map(p=>{
                      const { data, valor } = fmtCompra(p);
                      return (
                        <tr key={p.id} style={{borderBottom:"1px solid var(--z-border)"}}>
                          <td className="td-p">{data}</td>
                          <td className="td-r">R$ {valor}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function MapaLojistasAtacadistas({ onVoltar }){
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionadoId, setSelecionadoId] = useState(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const coresRef = useRef({});

  useEffect(()=>{
    setLoading(true);
    Promise.all([
      api.get("/clientes/contatos/?categoria=LOJISTA&page_size=1000"),
      api.get("/clientes/contatos/?categoria=ATACADISTA&page_size=1000"),
    ]).then(([lojR, ataR])=>{
      const lojistas = lojR.data.results || lojR.data;
      const atacadistas = ataR.data.results || ataR.data;
      setContatos([...lojistas, ...atacadistas]);
    }).catch(() => setContatos([]))
      .finally(() => setLoading(false));
  },[]);

  useEffect(() => {
    if (mapRef.current) return;
    const el = containerRef.current;
    if (!el) return;

    const map = L.map(el).setView([-23.5505, -46.6333], 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(map);
    const layerGroup = L.layerGroup().addTo(map);

    mapRef.current = map;
    layerRef.current = layerGroup;

    setTimeout(() => { try{ map.invalidateSize(); }catch{} }, 200);

    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!mapRef.current || !layerRef.current) return;

    layerRef.current.clearLayers();
    markersRef.current = {};
    coresRef.current = {};
    const pontos = [];

    contatos.forEach(c=>{
      const coord = coordValida(endPrincipal(c));
      if(!coord) return;
      const cor = corPorStatus(c);
      coresRef.current[c.id] = cor;

      const telefone = telPrincipal(c);
      const enderecoTxt = enderecoCompleto(endPrincipal(c));
      const categoriaLabel = c.categoria === "LOJISTA" ? "Lojista" : "Atacadista";
      const comprasId = "compras-"+c.id;

      let linhasExtra = "";
      if(c.status === "PROSPECCAO"){
        const ultimaData = c.data_ultima_interacao ? new Date(c.data_ultima_interacao+"T12:00:00").toLocaleDateString("pt-BR") : "Nunca contatado";
        const similar = c.comercializa_similar ? "Sim" : "Nao";
        linhasExtra = `<div><i>Ultimo contato:</i> ${ultimaData}</div><div><i>Comercializa similar:</i> ${similar}</div>`;
      }

      const html = `
        <div style="min-width:230px">
          <strong>${c.nome}</strong><br/>
          <span style="font-size:12px">${categoriaLabel}</span> -
          <span style="font-size:12px;color:${cor};font-weight:600">${statusLabel(c)}</span>
          <hr style="margin:6px 0;border:none;border-top:1px solid #e5e7eb"/>
          <div style="font-size:12px;line-height:1.6">
            ${linhasExtra}
            <div><i>Telefone:</i> ${telefone}</div>
            <div><i>Endereco:</i> ${enderecoTxt}</div>
            <div id="${comprasId}"><i>Ultimas compras:</i> carregando...</div>
          </div>
        </div>
      `;

      const marker = L.marker(coord,{icon: criarIcone(cor, false)});
      marker.bindPopup(html);
      marker.on("click", () => setSelecionadoId(c.id));
      marker.on("popupopen", async () => {
        const elCompras = document.getElementById(comprasId);
        if(!elCompras) return;
        try{
          const res = await api.get("/vendas/pedidos/?cliente="+c.id+"&page_size=3");
          const pedidos = res.data.results || res.data;
          if(pedidos.length){
            elCompras.innerHTML = "<i>Ultimas compras:</i><br/>" + pedidos.map(p=>{
              const { data, valor } = fmtCompra(p);
              return `${data} - R$ ${valor}`;
            }).join("<br/>");
          } else {
            elCompras.innerHTML = "<i>Ultimas compras:</i> nenhuma registrada";
          }
        }catch{
          elCompras.innerHTML = "<i>Ultimas compras:</i> erro ao carregar";
        }
      });

      layerRef.current.addLayer(marker);
      markersRef.current[c.id] = marker;
      pontos.push(coord);
    });

    if(pontos.length > 0){
      mapRef.current.fitBounds(pontos, { padding: [30,30], maxZoom: 14 });
    }
  }, [contatos, loading]);

  // Destaca a bolinha selecionada (aumenta o tamanho) e centraliza o mapa nela
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, marker])=>{
      const cor = coresRef.current[id];
      const destaque = String(id) === String(selecionadoId);
      marker.setIcon(criarIcone(cor, destaque));
    });

    if(selecionadoId != null && markersRef.current[selecionadoId] && mapRef.current){
      const marker = markersRef.current[selecionadoId];
      mapRef.current.panTo(marker.getLatLng());
    }
  }, [selecionadoId]);

  const selecionarCliente = (c) => {
    setSelecionadoId(prev => prev === c.id ? prev : c.id);
    const marker = markersRef.current[c.id];
    if(marker && mapRef.current){
      mapRef.current.panTo(marker.getLatLng());
      marker.openPopup();
    }
  };

  return (
    <div>
      <div className="page-toolbar">
        <button className="btn btn-secondary" onClick={onVoltar}><i className="ti ti-arrow-left"/> Voltar para lista</button>
      </div>

      <div style={{display:"flex", gap:16, flexWrap:"wrap", alignItems:"flex-start", marginBottom:16}}>
        <div style={{flex:"1 1 520px", minWidth:320}}>
          <div ref={containerRef} id="mapa-lojistas-atacadistas" style={{height:560, borderRadius:8, border:"1px solid var(--z-border)"}}/>
          <div style={{fontSize:11,color:"var(--z-text-muted)",marginTop:8,display:"flex",gap:16,flexWrap:"wrap"}}>
            <span><span style={{display:"inline-block",width:10,height:10,background:"#16a34a",borderRadius:"50%",marginRight:4,verticalAlign:"middle"}}/>Ativo</span>
            <span><span style={{display:"inline-block",width:10,height:10,background:"#f59e0b",borderRadius:"50%",marginRight:4,verticalAlign:"middle"}}/>Prospeccao - ja contatado</span>
            <span><span style={{display:"inline-block",width:10,height:10,background:"#dc2626",borderRadius:"50%",marginRight:4,verticalAlign:"middle"}}/>Prospeccao - nunca contatado</span>
            <span><span style={{display:"inline-block",width:10,height:10,background:"#6b7280",borderRadius:"50%",marginRight:4,verticalAlign:"middle"}}/>Inativo/Bloqueado</span>
          </div>
        </div>

        <div style={{flex:"1 1 380px", minWidth:300}}>
          <div className="card">
            <div className="table-wrapper" style={{maxHeight:560, overflowY:"auto"}}>
              {loading ? <div className="table-empty">Carregando...</div> :
              !contatos.length ? <div className="table-empty">Nenhum lojista/atacadista cadastrado.</div> : (
                <table>
                  <thead><tr>
                    <th>Nome</th><th>Categoria</th><th>Status</th><th>Telefone</th><th>Ultima interacao</th>
                  </tr></thead>
                  <tbody>
                    {contatos.map(c=>(
                      <LinhaCliente key={c.id} c={c} selecionado={selecionadoId===c.id} onSelecionar={selecionarCliente}/>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
